import React, { useState } from "react";
import axios from "axios";
import { Send, CheckCircle2, AlertCircle, User, Phone, Mail, GraduationCap, MapPin } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Quick registration form — posts directly to /api/leads.
 * Bilingual HE + AR. Includes hot-lead detection (interestStatus = hot).
 */
export default function RegistrationForm({ lang, courses = [] }) {
    const isAr = lang === "ar";

    const [form, setForm] = useState({
        fullName: "",
        phone: "",
        email: "",
        city: "",
        preferredCourse: "",
        notes: "",
    });
    const [status, setStatus] = useState("idle"); // idle | submitting | success | error
    const [errorMsg, setErrorMsg] = useState("");

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.fullName.trim() || !form.phone.trim() || !form.preferredCourse) {
            setStatus("error");
            setErrorMsg(isAr ? "الرجاء تعبئة الحقول الإجبارية" : "אנא מלא את השדות החובה");
            return;
        }
        setStatus("submitting");
        try {
            await axios.post(`${API}/leads`, {
                ...form,
                rawInputChannel: "Web Chat",
                preferredLanguage: lang,
                interestStatus: "hot", // form submissions = hot leads
            });
            setStatus("success");
            setForm({ fullName: "", phone: "", email: "", city: "", preferredCourse: "", notes: "" });
        } catch (err) {
            setStatus("error");
            setErrorMsg(
                err?.response?.data?.detail ||
                (isAr ? "حدث خطأ. حاول مجدداً أو اتصل بنا." : "אירעה שגיאה. נסה שוב או צור קשר.")
            );
        }
    };

    return (
        <section className="mb-16 relative" data-testid="registration-form">
            <div
                className="relative rounded-3xl overflow-hidden border border-[#1A365D]/30 shadow-[0_20px_60px_rgba(11,31,61,0.15)]"
                style={{
                    background: "radial-gradient(ellipse at 80% 20%, rgba(212,118,31,0.35) 0%, transparent 55%), linear-gradient(135deg, #0B1F3D 0%, #1A365D 100%)",
                }}
            >
                {/* Grid pattern */}
                <div
                    className="absolute inset-0 opacity-[0.08]"
                    style={{
                        backgroundImage: "linear-gradient(rgba(212,175,55,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.5) 1px, transparent 1px)",
                        backgroundSize: "44px 44px",
                    }}
                />

                <div className="relative z-10 grid grid-cols-1 lg:grid-cols-5 gap-0">
                    {/* Left copy panel */}
                    <div className="lg:col-span-2 p-8 md:p-12 border-b lg:border-b-0 lg:border-e border-white/10">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4761F]/20 border border-[#D4761F]/40 text-[#F8D89C] text-[10px] font-bold uppercase tracking-[0.2em] mb-4">
                            {isAr ? "تسجيل سريع" : "רישום מהיר"}
                        </div>
                        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white leading-tight mb-4">
                            {isAr ? "املأ التفاصيل — سنعاود الاتصال بك خلال 24 ساعة" : "השאר פרטים — נחזור אליך תוך 24 שעות"}
                        </h2>
                        <p className="text-white/80 text-sm leading-relaxed">
                            {isAr
                                ? "بدون التزام. مستشار الأهلية سيتواصل معك شخصياً، يستمع لأهدافك، ويقترح المسار الأنسب لك — بالعربية أو العبرية."
                                : "ללא התחייבות. יועץ אל-אהלייה יצור איתך קשר אישית, ישאל על המטרות שלך ויציע את המסלול המתאים ביותר — בערבית או בעברית."}
                        </p>
                        <ul className="mt-6 space-y-2.5 text-white/90 text-sm">
                            <li className="flex items-center gap-2">
                                <CheckCircle2 size={16} className="text-[#D4AF37] shrink-0" />
                                {isAr ? "استشارة مجانية" : "ייעוץ חינם"}
                            </li>
                            <li className="flex items-center gap-2">
                                <CheckCircle2 size={16} className="text-[#D4AF37] shrink-0" />
                                {isAr ? "بدون بسيخومتري" : "ללא פסיכומטרי"}
                            </li>
                            <li className="flex items-center gap-2">
                                <CheckCircle2 size={16} className="text-[#D4AF37] shrink-0" />
                                {isAr ? "منح تصل حتى 12,000 ₪" : "מלגות עד 12,000 ₪"}
                            </li>
                            <li className="flex items-center gap-2">
                                <CheckCircle2 size={16} className="text-[#D4AF37] shrink-0" />
                                {isAr ? "مرافقة شخصية بالعربية والعبرية" : "ליווי אישי בערבית ובעברית"}
                            </li>
                        </ul>
                    </div>

                    {/* Right form panel */}
                    <div className="lg:col-span-3 p-8 md:p-12">
                        {status === "success" ? (
                            <div className="h-full flex flex-col items-center justify-center text-center py-10" data-testid="registration-success">
                                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#22C55E] to-[#16A34A] flex items-center justify-center mb-4 shadow-[0_10px_30px_rgba(34,197,94,0.4)]">
                                    <CheckCircle2 size={32} className="text-white" />
                                </div>
                                <h3 className="font-serif text-2xl font-bold text-white mb-2">
                                    {isAr ? "تم استلام تفاصيلك!" : "הפרטים התקבלו!"}
                                </h3>
                                <p className="text-white/80 text-sm max-w-sm">
                                    {isAr
                                        ? "سنتواصل معك خلال 24 ساعة. حتى ذلك الوقت — يمكنك تصفح مسارات الدراسة أو الدردشة مع البوت."
                                        : "ניצור איתך קשר תוך 24 שעות. עד אז — אפשר לעיין במסלולי הלימוד או לצ'אט עם הבוט."}
                                </p>
                                <button
                                    onClick={() => setStatus("idle")}
                                    className="mt-6 text-[#F8D89C] hover:text-white text-sm font-semibold underline decoration-[#D4AF37]/50 hover:decoration-white transition"
                                >
                                    {isAr ? "تسجيل شخص آخر" : "רישום אדם נוסף"}
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4" data-testid="registration-form-fields">
                                {/* Name + Phone (row) */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field
                                        icon={User}
                                        label={isAr ? "الاسم الكامل *" : "שם מלא *"}
                                        name="fullName"
                                        value={form.fullName}
                                        onChange={handleChange}
                                        required
                                        testId="input-fullname"
                                    />
                                    <Field
                                        icon={Phone}
                                        label={isAr ? "هاتف *" : "טלפון *"}
                                        name="phone"
                                        type="tel"
                                        value={form.phone}
                                        onChange={handleChange}
                                        required
                                        testId="input-phone"
                                    />
                                </div>

                                {/* Email + City (row) */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field
                                        icon={Mail}
                                        label={isAr ? "بريد إلكتروني" : "אימייל"}
                                        name="email"
                                        type="email"
                                        value={form.email}
                                        onChange={handleChange}
                                        testId="input-email"
                                    />
                                    <Field
                                        icon={MapPin}
                                        label={isAr ? "مدينة السكن" : "יישוב מגורים"}
                                        name="city"
                                        value={form.city}
                                        onChange={handleChange}
                                        testId="input-city"
                                    />
                                </div>

                                {/* Preferred course */}
                                <div className="relative">
                                    <label className="block text-white/80 text-xs font-bold mb-1.5">
                                        {isAr ? "المسار المطلوب *" : "מסלול מבוקש *"}
                                    </label>
                                    <div className="relative">
                                        <GraduationCap size={16} className="absolute top-1/2 -translate-y-1/2 start-3 text-[#F8D89C]" />
                                        <select
                                            name="preferredCourse"
                                            value={form.preferredCourse}
                                            onChange={handleChange}
                                            required
                                            data-testid="input-course"
                                            className="w-full ps-10 pe-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition"
                                        >
                                            <option value="" className="bg-[#0B1F3D]">
                                                {isAr ? "-- اختر مسارًا --" : "-- בחר מסלול --"}
                                            </option>
                                            {courses.map((c) => (
                                                <option key={c.id} value={c.id} className="bg-[#0B1F3D]">
                                                    {isAr ? c.nameAr : c.nameHe}
                                                </option>
                                            ))}
                                            <option value="unsure" className="bg-[#0B1F3D]">
                                                {isAr ? "غير متأكد — أحتاج استشارة" : "לא בטוח/ה — צריך/ה ייעוץ"}
                                            </option>
                                        </select>
                                    </div>
                                </div>

                                {/* Notes */}
                                <div>
                                    <label className="block text-white/80 text-xs font-bold mb-1.5">
                                        {isAr ? "ملاحظات (اختياري)" : "הערות (רשות)"}
                                    </label>
                                    <textarea
                                        name="notes"
                                        value={form.notes}
                                        onChange={handleChange}
                                        rows={2}
                                        data-testid="input-notes"
                                        placeholder={isAr ? "أي معلومات إضافية تريدنا أن نعرفها؟" : "מידע נוסף שכדאי לנו לדעת?"}
                                        className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition resize-none"
                                    />
                                </div>

                                {/* Error */}
                                {status === "error" && errorMsg && (
                                    <div className="flex items-start gap-2 text-[#FCA5A5] text-xs bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="registration-error">
                                        <AlertCircle size={14} className="shrink-0 mt-0.5" />
                                        {errorMsg}
                                    </div>
                                )}

                                {/* Submit */}
                                <button
                                    type="submit"
                                    disabled={status === "submitting"}
                                    data-testid="registration-submit"
                                    className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#D4761F] to-[#E08A30] hover:from-[#B36318] hover:to-[#D4761F] disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-lg font-bold text-sm shadow-[0_10px_30px_rgba(212,118,31,0.4)] transition-all hover:scale-[1.02]"
                                >
                                    {status === "submitting" ? (
                                        <>{isAr ? "جارٍ الإرسال..." : "שולח..."}</>
                                    ) : (
                                        <>
                                            <Send size={16} />
                                            {isAr ? "أرسل تفاصيلي — سيتواصلون معي" : "שלח פרטים — יחזרו אליי"}
                                        </>
                                    )}
                                </button>

                                <p className="text-white/50 text-[10px] text-center leading-relaxed">
                                    {isAr
                                        ? "بإرسال هذا النموذج فإنك توافق على سياسة الخصوصية وشروط الاستخدام."
                                        : "בשליחת הטופס את/ה מסכימ/ה למדיניות הפרטיות ותנאי השירות."}
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

// ── Reusable field ────────────────────────────────────────────
function Field({ icon: Icon, label, name, type = "text", value, onChange, required, testId }) {
    return (
        <div>
            <label className="block text-white/80 text-xs font-bold mb-1.5">{label}</label>
            <div className="relative">
                <Icon size={16} className="absolute top-1/2 -translate-y-1/2 start-3 text-[#F8D89C]" />
                <input
                    type={type}
                    name={name}
                    value={value}
                    onChange={onChange}
                    required={required}
                    data-testid={testId}
                    className="w-full ps-10 pe-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition"
                />
            </div>
        </div>
    );
}
